import { Inject, Options, Vue, Watch } from "vue-property-decorator";
import { AuthorizedUser } from '@/trust-admin-app/models/authorized-user';
import store from '@/trust-admin-app/store';
import { TrustAPI } from '@/shared/modules/trust-api';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { orderBy } from 'lodash';
import AccessValidationMixin from '@/shared/mixins/access-validation';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import { EventEmitter } from 'events';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import debounce from 'lodash/debounce';
import { InvestigationTypeView, FollowUpStatus, FollowUpFilterRequest, FollowUpView } from '@/shared/modules/typescript-trustapi-client/models';
import Tag from '@/shared/components/tag/index.vue';
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import { AppInsights } from "@/trust-admin-app/appInsights";

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
    Tag,
    DatePicker,
  },
  mixins: [
    AccessValidationMixin,
    FormatDateTimeMixin
  ]
})
export default class FollowUp extends Vue {
  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights;

  private searchKeyword = '';
  private showActiveOnly = true;
  private noInvestigatorAssigned = false;
  private sortMethod = 'desc';
  private activeSortHeader = 2;
  private showCauseDrivenOnly = false;
  private isReplyRequired = false;

  private loadingContent = false;
  private errorLoadingContent = false;
  private loadingDataSet = false;
  private errorLoadingDataSet = false;
  private showAdvancedFilter = false;
  private filterApplied = false;

  private closingDateRange = [null, null];
  private reinvestigationDateRange = [null, null];

  private advanceFilter: any = {
    keyword: null,
    statusId: null,
    followUpTypeId: null,
    closingDateFrom: null,
    closingDateTo: null,
    reInvestigationDateFrom: null,
    reInvestigationDateTo: null,
    investigationTypeId: null,
    period: null,
    take: 100,
  };

  private period = [3, 6, 12, 18, 24, 36, 48];
  private srchDebounced: any = null;

  @Watch('searchKeyword')
  private onKeywordChange(): void {
    setTimeout(this.srchDebounced.cancel, 0);
    setTimeout(this.srchDebounced, 0);
  }

  private tableHeadings = [
    { text: 'Practice (Trading name)', value: 'name', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Practice - branch ID', value: 'practiceBranchId', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Closing date', value: 'closingDate', sortMethod: 'desc', sortable: true, activeSort: false },
    { text: 'Last inv type', value: 'invDate', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Re inv date', value: 'reInvDate', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Investigation type', value: 'investigationTypeName', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Action', value: '', sortMethod: 'asc', sortable: false, activeSort: false },
  ];

  private investigationTypes: InvestigationTypeView[] = [];

  private followUps: FollowUpView[] = [];

  private followUpStatuses: any[] = [];

  private followUpsDisplay = this.followUps;
  private radioFilter = 1;

  created(): void {
    this.preloadData();
  }

  mounted(): void {
    window.scrollTo(0, 0);
    // this.eventCtrl.on('reload-investigation-data', this.loadFollowups);
    this.srchDebounced = debounce(() => {
      this.loadFollowUps();
    }, 1000);
  }

  // beforeUnmount(): void {
  //   this.eventCtrl.removeListener('reload-investigation-data', this.loadFollowups);
  // }

  private async preloadData(): Promise<void> {
    try {
      this.loadingDataSet = true;
      this.errorLoadingDataSet = false;
      const resp: any[] = await Promise.all([
        this.apiService.ConfigTrustApi?.apiV2ConfigTrustGetInvestigationTypesPost(),
        this.apiService.CodeApi?.apiV2CodeGetFollowUpStatusesGet(),
        // this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetSupervisorsGet(),
      ]);
      this.investigationTypes = resp[0].data.data as InvestigationTypeView[];
      this.followUpStatuses = resp[1].data.data as FollowUpStatus[];
      this.loadFollowUps();

    } catch (err) {
      this.errorLoadingDataSet = true;
      this.ai.trackException('Error, Unable to preload data: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to preload data');
    } finally {
      this.loadingDataSet = false;
    }
  }

  private async loadFollowUps(): Promise<boolean> {
    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;
      const request: FollowUpFilterRequest = {
        keyword: this.searchKeyword,
        statusId: null,
        followUpTypeId: null,
        closingDateFrom: null,
        closingDateTo: null,
        reInvestigationDateFrom: null,
        reInvestigationDateTo: null,
        investigationTypeId: null,
        period: null,
        take: 100,
      };

      if (this.filterApplied) {
        request.closingDateFrom = this.closingDateRange[0];
        request.closingDateTo = this.closingDateRange[1];
        request.reInvestigationDateFrom = this.reinvestigationDateRange[0];
        request.reInvestigationDateTo = this.reinvestigationDateRange[1];
        request.investigationTypeId = this.advanceFilter.investigationTypeId;
        request.period = this.advanceFilter.period;
        request.statusId = this.advanceFilter.statusId;
      }

      const resp: any =
        await this.apiService.FollowUpApi?.apiV2FollowUpGetFollowUpsPost(
          request
        );

      this.followUps = resp.data.data as FollowUpView[];

      this.followUpsDisplay = [...this.followUps];

      // convert to display table
      // for (let i = 0; i < this.followUps.length; i++) {
      //   this.followUpsDisplay[i].name = this.followUps[i].practice?.branchId ?
      //     this.followUps[i].practice.name + ' (' + this.followUps[i].practice.tradingName + ')' :
      //     this.followUps[i].practice.name;
      //   this.followUpsDisplay[i].practiceBranchId = this.followUps[i].practice.branchId ?
      //     this.followUps[i].practice.firmId + ' - ' + this.followUps[i].practice.branchId :
      //     this.followUps[i].practice.firmId.toString();
      //   this.followUpsDisplay[i].investigationTypeName = this.followUps[i].investigationType.name + ' (' + this.followUps[i].investigationCategory.abbreviation + ')';
      // }

      return Promise.resolve(false);
    } catch (err) {
      this.errorLoadingContent = true;
      console.log(err);
      this.ai.trackException('Error, Unable to get followUps list: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to get followUps list');
      return Promise.reject(true);
    } finally {
      this.loadingContent = false;
    }
  }

  private applyFilter(): void {
    this.filterApplied = true;
    this.loadFollowUps();
  }

  private resetFilter(): void {
    this.filterApplied = false;
    this.searchKeyword = '';
    this.closingDateRange = [null, null];
    this.reinvestigationDateRange = [null, null];
    this.advanceFilter = {
      investigationTypeId: null,
      period: null,
      statusId: null,
    };
    this.loadFollowUps();
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  get usrAcct(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get RouteN(): typeof RouteN {
    return RouteN;
  }

  private sortTableColumn(index: number, sortable: boolean) {
    if (sortable) {
      if (this.tableHeadings[index].sortable) {
        this.activeSortHeader = index;
        this.sortMethod = this.sortMethod === 'desc' ? 'asc' : 'desc';
        this.tableHeadings[index].sortMethod = this.sortMethod;
        if (this.followUpsDisplay.length > 0) {
          const nameSorter = index == 0 ? (c: any) => c.name.toLowerCase() : [this.tableHeadings[index].value, (c: any) => c.name.toLowerCase()];
          const orderByResults = orderBy(
            this.followUpsDisplay,
            nameSorter,
            [this.sortMethod as any]
          );
          this.followUpsDisplay = orderByResults;
        }
      }
    }
  }

  private approve(f: FollowUpView) {
    this.loadingContent = true;
    this.apiService.FollowUpApi?.apiV2FollowUpApproveRoutineFollowUpPut(f.id)
      .then(() => {
        this.eventCtrl.emit('show-snackbar', 'success', 'Follow Up approved');
        this.loadFollowUps();
        this.loadingContent = false;
      })
      .catch(err => {
        this.ai.trackException('Error, Unable to approve this follow up: ' + err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to approve this follow up');
        this.loadingContent = false;
      })
  }

  private reject(f: FollowUpView) {
    this.loadingContent = true;
    this.apiService.FollowUpApi?.apiV2FollowUpRejectRoutineFollowUpPut(f.id)
      .then(() => {
        this.eventCtrl.emit('show-snackbar', 'success', 'Follow Up rejected');
        this.loadFollowUps();
        this.loadingContent = false;
      })
      .catch(err => {
        this.ai.trackException('Error, Unable to reject this follow up: ' + err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to reject this follow up');
        this.loadingContent = false;
      })
  }

}

